import { useEffect, useState } from "react";
import { Button } from "./ui/button";
import { Card } from "./ui/card";
import { Label } from "./ui/label";
import { Input } from "./ui/input";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Info } from "lucide-react";
import {
  GiftSuggestionForm,
  GiftFormData,
  createEmptyGiftFormData,
  formDataToApiPayload,
} from "./GiftSuggestionForm";

interface Giftee {
  id: string | number;
  name: string;
  email: string;
}

interface Recipient {
  id: string | number;
  userId?: number; // optional when guest/member has no linked user account
  name: string;
}

interface GiftSuggestionsProps {
  giftGroupId: string | null;
  onSuggestionsAdded?: () => void;
  // Keep these in the props type so callers can pass them (App.tsx, GiftGroupOverview, etc.)
  onNavigate?: (screen: string, data?: {
    giftGroupId?: string;
    memberId?: string;
    tab?: string;
    view?: string;
    suggestionId?: string;
    refreshDashboard?: boolean;
    recommendedAmount?: number;
  }) => void;
  embedded?: boolean;
}

export function GiftSuggestions(props: GiftSuggestionsProps) {
  // Destructure only the props we use locally to avoid TS "declared but never used" warnings.
  const { giftGroupId, onSuggestionsAdded } = props;

  const [url, setUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [showManualForm, setShowManualForm] = useState(false);
  const [giftees, setGiftees] = useState<Giftee[]>([]);
  const [recipients, setRecipients] = useState<Recipient[]>([]);
  const [groupTypeId, setGroupTypeId] = useState<number | null>(null);
  const [currentUserId, setCurrentUserId] = useState<number | null>(null);
  const [manualGift, setManualGift] = useState<GiftFormData & { gifteeId: string }>({
    ...createEmptyGiftFormData(),
    gifteeId: "", // stored as string for select; convert on submit
  });

  // Load current user early so we can filter dropdown reliably
  useEffect(() => {
    const fetchCurrentUser = async () => {
      try {
        const user = await api.getCurrentUser();
        setCurrentUserId(Number(user.id));
      } catch (err) {
        console.error("[GiftSuggestions] Failed to load current user", err);
      }
    };
    fetchCurrentUser();
  }, []);

  // Fetch group info (members/giftees) when giftGroupId changes
  useEffect(() => {
    const fetchGroupInfo = async () => {
      if (!giftGroupId) return;
      try {
        const response = await api.getGiftGroup(giftGroupId);
        const typeId = Number(response.type_id);
        setGroupTypeId(typeId);

        // Types 1-3: giftees list
        if (typeId >= 1 && typeId <= 3) {
          const gifteeMembers = (response.members || []).filter((m: any) => m.role === "giftee");
          setGiftees(
            gifteeMembers.map((m: any) => ({
              id: m.id || m.user_id,
              name: m.name || m.email || String(m.contact || ""),
              email: m.email || m.contact || "",
            }))
          );
        }

        // Types 4 & 6: recipients (everyone except self when rendering)
        if (typeId === 4 || typeId === 6) {
          const allMembers = response.members || [];
          setRecipients(
            allMembers.map((m: any) => ({
              id: m.id,
              userId: m.user_id ? Number(m.user_id) : undefined,
              name: m.name || m.email || String(m.contact || ""),
            }))
          );
        }
      } catch (error) {
        console.error("[GiftSuggestions] Failed to fetch group info:", error);
      }
    };

    fetchGroupInfo();
  }, [giftGroupId]);

  // Quick-add via URL (scrape)
  const handleQuickAdd = async () => {
    if (!giftGroupId || !url) return;
    setLoading(true);
    try {
      // Use the frontend API wrapper name (addGiftSuggestion)
      const res: any = await api.addGiftSuggestion(giftGroupId, { url });

      if (res?.needsEditing) {
        // Populate manual form with scraped data and show it
        const suggestion = res.suggestion || {};
        setManualGift({
          url: suggestion.url || url,
          name: suggestion.name || "",
          description: suggestion.description || "",
          price: suggestion.price != null ? String(suggestion.price) : "",
          imageUrl: suggestion.imageUrl || "",
          shippingCost: suggestion.shippingCost != null ? String(suggestion.shippingCost) : "",
          estimatedDeliveryDays:
            suggestion.estimatedDeliveryDays != null
              ? String(suggestion.estimatedDeliveryDays)
              : "",
          suggestedOrderDate: suggestion.suggestedOrderDate || "",
          gifteeId: suggestion.gifteeId != null ? String(suggestion.gifteeId) : "",
        });
        setShowManualForm(true);
      } else {
        toast.success("Suggestion added");
        setUrl("");
        if (onSuggestionsAdded) onSuggestionsAdded();
      }
    } catch (error) {
      console.error("Quick add failed:", error);
      toast.error("Failed to add suggestion");
    } finally {
      setLoading(false);
    }
  };

  // Submit manual suggestion
  const handleManualSubmit = async () => {
    if (!giftGroupId) return;
    setLoading(true);
    try {
      // Convert gifteeId to number or undefined (backend accepts number or null; undefined plays well with ApiClient)
      const gifteeIdValue = manualGift.gifteeId ? Number(manualGift.gifteeId) : undefined;
      const payload = formDataToApiPayload(manualGift, "manual");
      await api.addGiftSuggestion(giftGroupId, {
        ...payload,
        url: manualGift.url || url,
        gifteeId: gifteeIdValue,
      });
      toast.success("Suggestion added");
      setShowManualForm(false);
      setManualGift({ ...createEmptyGiftFormData(), gifteeId: "" });
      setUrl("");
      if (onSuggestionsAdded) onSuggestionsAdded();
    } catch (error) {
      console.error("Manual submit failed:", error);
      toast.error("Failed to add suggestion");
    } finally {
      setLoading(false);
    }
  };

  // Helper to render giftee selector
  const renderGifteeSelector = () => {
    // Recipient dropdown for Exchange (4) and Gift Circle (6)
    if ((groupTypeId === 4 || groupTypeId === 6) && recipients.length > 0 && currentUserId !== null) {
      return (
        <div className="space-y-2 p-3 bg-primary/5 border border-primary/20 rounded-md">
          <Label htmlFor="giftee-select">This Gift Is For *</Label>
          <select
            id="giftee-select"
            value={manualGift.gifteeId || ""}
            onChange={(e) => setManualGift({ ...manualGift, gifteeId: e.target.value })}
            className="w-full p-2 border rounded-md bg-white"
          >
            <option value="">Select who this gift is for</option>
            {recipients
              .filter((r) => {
                // Exclude current user by numeric comparison when userId exists
                if (r.userId == null) return true;
                return Number(r.userId) !== Number(currentUserId);
              })
              .map((recipient) => (
                <option
                  key={recipient.id}
                  value={recipient.userId != null ? String(recipient.userId) : String(recipient.id)}
                >
                  {recipient.name}
                </option>
              ))}
          </select>
          <div className="text-xs text-muted-foreground">
            {groupTypeId === 4
              ? "🎁 In Gift Exchange, everyone can suggest gifts for anyone (except themselves)."
              : "🎁 In a Gift Circle, everyone suggests gifts for other members."}
          </div>
        </div>
      );
    }

    // For traditional types (1-3), show giftee dropdown if multiple giftees exist
    if (groupTypeId !== null && groupTypeId <= 3 && giftees.length > 1) {
      return (
        <div>
          <Label htmlFor="giftee-traditional">This Gift Is For</Label>
          <select
            id="giftee-traditional"
            value={manualGift.gifteeId || ""}
            onChange={(e) => setManualGift({ ...manualGift, gifteeId: e.target.value })}
            className="w-full p-2 border rounded-md bg-white mt-1"
          >
            <option value="">Select giftee</option>
            {giftees.map((g) => (
              <option key={g.id} value={String(g.id)}>
                {g.name}
              </option>
            ))}
          </select>
        </div>
      );
    }

    return null;
  };

  return (
    <div>
      <Card className="p-4 mb-4">
        <div className="mb-2">
          <Label htmlFor="suggest-url">Product URL *</Label>
          <Input
            id="suggest-url"
            value={url}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setUrl(e.target.value)}
            placeholder="https://..."
          />
        </div>

        <div className="flex gap-2">
          <Button onClick={handleQuickAdd} disabled={loading || !url} className="bg-primary">
            {loading ? "Adding..." : "Add Gift (Scrape URL)"}
          </Button>
          <Button onClick={() => setShowManualForm((s) => !s)} variant="outline">
            {showManualForm ? "Hide Form" : "Add Manually"}
          </Button>
        </div>
      </Card>

      {showManualForm && (
        <GiftSuggestionForm
          data={manualGift}
          onChange={(newData) => setManualGift({ ...newData, gifteeId: manualGift.gifteeId })}
          onSubmit={handleManualSubmit}
          onCancel={() => setShowManualForm(false)}
          isSubmitting={loading}
          submitLabel="Add Suggestion"
          header={{
            title: "Add Gift Suggestion",
            subtitle: "Enter the gift details manually",
          }}
          additionalContent={renderGifteeSelector()}
        />
      )}

      <Card className="p-4 mt-6">
        <div className="flex items-center gap-2 mb-2">
          <Info className="w-4 h-4" />
          <div>Paste any product URL and we'll automatically extract details. You can review & edit before adding.</div>
        </div>
      </Card>
    </div>
  );
}
